import lodash from 'lodash'
import { pi } from 'mathjs'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoPipeCircumferentialWeld,
  GeckoPipeCircumferentialWeldJSON,
  GeckoPipeIntegralSupport,
  GeckoPipeIntegralSupportJSON,
  GeckoPipeNonIntegralSupport,
  GeckoPipeNonIntegralSupportJSON,
  GeckoPipeSeamWeld,
  GeckoPipeSeamWeldJSON,
  GeckoPipeWrap,
  GeckoPipeWrapJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoFixedSensor,
  GeckoFixedSensorJSON,
} from 'types/assetModel/features/geckoFixedSensor'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalDateStringWithRegex,
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export enum GeckoPipeFlowDirection {
  Positive = 'positive',
  Negative = 'negative',
  Unknown = 'unknown',
}

type PipeSectionSupportedFeatures =
  | GeckoFixedSensorJSON
  | GeckoNozzleJSON
  | GeckoPipeNonIntegralSupportJSON
  | GeckoPipeIntegralSupportJSON
  | GeckoPipeCircumferentialWeldJSON
  | GeckoPipeSeamWeldJSON
  | GeckoPipeWrapJSON
  | GeckoPlateJSON
  | GeckoVerticalWeldJSON
  | GeckoHorizontalWeldJSON

export type PipeSectionAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.PipeSection
  radius: number
  length: number
  material: string | undefined
  installation_date: string | undefined
  joint_efficiency: number | undefined
  stress_tolerance: number | undefined

  flow_direction: GeckoPipeFlowDirection | undefined
  clock_bearing_rotation: number | undefined
  features: Record<string, PipeSectionSupportedFeatures>
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  nominal: {
    wall_thickness?: number
  }
  tmin: number | undefined
  compass_orientation?: number
}

export const PipeSectionAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.Plate,
  GeckoFeatureModelType.VerticalWeld,
  GeckoFeatureModelType.HorizontalWeld,
  GeckoFeatureModelType.Nozzle,
  GeckoFeatureModelType.FixedSensor,
  GeckoFeatureModelType.PipeNonIntegralSupport,
  GeckoFeatureModelType.PipeIntegralSupport,
  GeckoFeatureModelType.PipeCircumferentialWeld,
  GeckoFeatureModelType.PipeSeamWeld,
  GeckoFeatureModelType.PipeWrap,
]

export const NewPipeSectionAssetModelJSON = {
  name: 'New Pipe Section',
  type: GeckoComponentModelType.PipeSection,
  radius: 0,
  length: 0,
  material: undefined,
  installation_date: undefined,
  joint_efficiency: undefined,
  stress_tolerance: undefined,

  flow_direction: undefined,
  clock_bearing_rotation: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  compass_orientation: undefined,
  nominal: {},
} as PipeSectionAssetModelJSON

export class GeckoPipeSection extends GeckoEditableComponent {
  type: GeckoComponentModelType.PipeSection
  radius: GeckoMeasurement
  length: GeckoMeasurement
  material: string | undefined
  installationDate: Date | undefined
  jointEfficiency: number | undefined
  stressTolerance: GeckoMeasurement | undefined

  flowDirection: GeckoPipeFlowDirection | undefined
  clockBearingRotation: number | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  nozzles: Map<string, GeckoNozzle>
  fixedSensors: Map<string, GeckoFixedSensor>
  plates: Map<string, GeckoPlate>
  verticalWelds: Map<string, GeckoVerticalWeld>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  pipeNonIntegralSupports: Map<string, GeckoPipeNonIntegralSupport>
  pipeIntegralSupports: Map<string, GeckoPipeIntegralSupport>
  pipeCircumferentialWelds: Map<string, GeckoPipeCircumferentialWeld>
  pipeSeamWelds: Map<string, GeckoPipeSeamWeld>
  pipeWraps: Map<string, GeckoPipeWrap>
  wallNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    radius: GeckoMeasurement,
    length: GeckoMeasurement,
    material: string | undefined,
    installationDate: Date | undefined,
    jointEfficiency: number | undefined,
    stressTolerance: GeckoMeasurement | undefined,
    flowDirection: GeckoPipeFlowDirection | undefined,
    clockBearingRotation: number | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    nozzles: Map<string, GeckoNozzle>,
    fixedSensors: Map<string, GeckoFixedSensor>,
    plates: Map<string, GeckoPlate>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    pipeNonIntegralSupports: Map<string, GeckoPipeNonIntegralSupport>,
    pipeIntegralSupports: Map<string, GeckoPipeIntegralSupport>,
    pipeCircumferentialWelds: Map<string, GeckoPipeCircumferentialWeld>,
    pipeSeamWelds: Map<string, GeckoPipeSeamWeld>,
    pipeWraps: Map<string, GeckoPipeWrap>,
    wallNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.PipeSection
    this.radius = radius
    this.length = length
    this.material = material
    this.installationDate = installationDate
    this.jointEfficiency = jointEfficiency
    this.stressTolerance = stressTolerance
    this.flowDirection = flowDirection
    this.clockBearingRotation = clockBearingRotation
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.nozzles = nozzles
    this.fixedSensors = fixedSensors
    this.plates = plates
    this.verticalWelds = verticalWelds
    this.horizontalWelds = horizontalWelds
    this.pipeNonIntegralSupports = pipeNonIntegralSupports
    this.pipeIntegralSupports = pipeIntegralSupports
    this.pipeCircumferentialWelds = pipeCircumferentialWelds
    this.pipeSeamWelds = pipeSeamWelds
    this.pipeWraps = pipeWraps
    this.wallNominalThickness = wallNominalThickness
    this.tmin = tmin
    this.compassOrientation = compassOrientation
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get fixedSensorsArray() {
    return Array.from(this.fixedSensors.values())
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public get pipeNonIntegralSupportsArray() {
    return Array.from(this.pipeNonIntegralSupports.values())
  }

  public get pipeIntegralSupportsArray() {
    return Array.from(this.pipeIntegralSupports.values())
  }

  public get pipeCircumferentialWeldsArray() {
    return Array.from(this.pipeCircumferentialWelds.values())
  }

  public get pipeSeamWeldsArray() {
    return Array.from(this.pipeSeamWelds.values())
  }

  public get pipeWrapsArray() {
    return Array.from(this.pipeWraps.values())
  }

  public static CreateFromJSON(id: string, json: PipeSectionAssetModelJSON) {
    const name = json.name as string
    const compassOrientation = json.compass_orientation
    const pipeRadius = GeckoMeasurement.CreateFromValue(
      json.radius as number,
      GeckoMeasurementUnit.Meters
    )
    const pipeLength = GeckoMeasurement.CreateFromValue(
      json.length as number,
      GeckoMeasurementUnit.Meters
    )
    const material = parseString(json.material)
    const installationDate = parseDate(json.installation_date)

    const jointEfficiency = parseNumber(json.joint_efficiency)
    const parsedStressTolerance = parseNumber(json.stress_tolerance)
    const stressTolerance =
      parsedStressTolerance !== undefined
        ? GeckoMeasurement.CreateFromValue(
            parsedStressTolerance,
            GeckoMeasurementUnit.Megapascals
          )
        : undefined
    const flowDirection = json.flow_direction as
      | GeckoPipeFlowDirection
      | undefined

    const clockBearingRotation = parseNumber(json.clock_bearing_rotation)

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const featureData = json.features as Record<
      string,
      PipeSectionSupportedFeatures
    >

    const childPlateJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Plate
    })
    const plates = GeckoPlate.CreateMapFromJSON(
      id,
      childPlateJSON as Record<string, GeckoPlateJSON>
    )

    const childHorizontalWeldJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const childNozzleJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    })
    const nozzles = GeckoNozzle.CreateMapFromJSON(
      id,
      childNozzleJSON as Record<string, GeckoNozzleJSON>
    )

    const childFixedSensorJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.FixedSensor
    })
    const fixedSensors = GeckoFixedSensor.CreateMapFromJSON(
      id,
      childFixedSensorJSON as Record<string, GeckoFixedSensorJSON>
    )

    const childPipeNonIntegralSupportJSON = lodash.pickBy(
      featureData,
      (child) => {
        return child.type === GeckoFeatureModelType.PipeNonIntegralSupport
      }
    )

    const pipeNonIntegralSupports =
      GeckoPipeNonIntegralSupport.CreateMapFromJSON(
        id,
        childPipeNonIntegralSupportJSON as Record<
          string,
          GeckoPipeNonIntegralSupportJSON
        >
      )

    const childPipeIntegralSupportJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeIntegralSupport
    })

    const pipeIntegralSupports = GeckoPipeIntegralSupport.CreateMapFromJSON(
      id,
      childPipeIntegralSupportJSON as Record<
        string,
        GeckoPipeIntegralSupportJSON
      >
    )

    const childPipeCircumferentialWeldJSON = lodash.pickBy(
      featureData,
      (child) => {
        return child.type === GeckoFeatureModelType.PipeCircumferentialWeld
      }
    )

    const pipeCircumferentialWelds =
      GeckoPipeCircumferentialWeld.CreateMapFromJSON(
        id,
        childPipeCircumferentialWeldJSON as unknown as Record<
          string,
          GeckoPipeCircumferentialWeldJSON
        >,
        componentBounds
      )
    const childPipeSeamWeldJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeSeamWeld
    })

    const pipeSeamWelds = GeckoPipeSeamWeld.CreateMapFromJSON(
      id,
      childPipeSeamWeldJSON as Record<string, GeckoPipeSeamWeldJSON>
    )

    const childPipeWrapJSON = lodash.pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PipeWrap
    })

    const pipeWraps = GeckoPipeWrap.CreateMapFromJSON(
      id,
      childPipeWrapJSON as Record<string, GeckoPipeWrapJSON>,
      componentBounds
    )
    const nominalJSON = json.nominal
    const parsedWallNominalThickness = parseNumber(nominalJSON.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const tminJson = json.tmin
    const parsedTmin = parseNumber(tminJson)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    return new GeckoPipeSection(
      id,
      json,
      name,
      pipeRadius,
      pipeLength,
      material,
      installationDate,
      jointEfficiency,
      stressTolerance,
      flowDirection,
      clockBearingRotation,
      componentBounds,
      sceneConfig,
      nozzles,
      fixedSensors,
      plates,
      verticalWelds,
      horizontalWelds,
      pipeNonIntegralSupports,
      pipeIntegralSupports,
      pipeCircumferentialWelds,
      pipeSeamWelds,
      pipeWraps,
      wallNominalThickness,
      tmin,
      compassOrientation
    )
  }

  public asJSON(): PipeSectionAssetModelJSON {
    return generatePipeSection(
      this.name,
      this.radius,
      this.length,
      this.material,
      this.installationDate,
      this.jointEfficiency,
      this.stressTolerance,
      this.flowDirection,
      this.clockBearingRotation,
      this.wallNominalThickness,
      this.tmin,
      this.compassOrientation,
      this.json as PipeSectionAssetModelJSON
    )
  }
}

const generatePipeSection = (
  name: string,
  radius: GeckoMeasurement,
  length: GeckoMeasurement,
  material: string | undefined,
  installationDate: Date | undefined,
  jointEfficiency: number | undefined,
  stressTolerance: GeckoMeasurement | undefined,
  flowDirection: GeckoPipeFlowDirection | undefined,
  clockBearingRotation: number | undefined,
  wallNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  otherPipeSectionData: PipeSectionAssetModelJSON = NewPipeSectionAssetModelJSON
): PipeSectionAssetModelJSON => {
  const pipeSectionLength = length
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const pipeSectionRadius = radius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const pipeSectionCircumference = GeckoMeasurement.CreateFromValue(
    pipeSectionRadius * 2 * pi,
    GeckoMeasurementUnit.Meters
  ).toPrecision(assetModelPrecision).num

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined

  const parsedStressTolerance =
    stressTolerance !== undefined
      ? stressTolerance
          .convertTo(GeckoMeasurementUnit.Megapascals)
          .toPrecision(assetModelPrecision).num
      : undefined

  return {
    name: name,
    type: GeckoComponentModelType.PipeSection,
    radius: pipeSectionRadius,
    length: pipeSectionLength,
    material: material,
    installation_date: parsedInstallationDate,
    joint_efficiency: jointEfficiency,
    stress_tolerance: parsedStressTolerance,
    flow_direction: flowDirection,
    clock_bearing_rotation: clockBearingRotation,
    features: otherPipeSectionData.features,
    scene_config: otherPipeSectionData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: pipeSectionCircumference,
      y_end: pipeSectionLength,
    },
    tmin: tminMeters,
    compass_orientation: compassOrientation,
    nominal: { wall_thickness: wallNominalThicknessMeters },
  }
}

export const PipeSectionAssetModelFormFields = (
  pipeSectionJSON: PipeSectionAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Pipe Section Name',
      value: pipeSectionJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'radius',
      label: 'Radius',
      value: pipeSectionJSON.radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'length',
      label: 'Length',
      value: pipeSectionJSON.length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material',
      value: pipeSectionJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'installation_date',
      label: 'Installation Date',
      value: pipeSectionJSON.installation_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Date,
    },
    {
      dataKey: 'joint_efficiency',
      label: 'Joint Efficiency',
      value: pipeSectionJSON.joint_efficiency,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'stress_tolerance',
      label: 'Stress Tolerance',
      value: pipeSectionJSON.stress_tolerance,
      unit_magnitude: UnitMagnitude.PsiOrMPa,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'flow_direction',
      label: 'Flow Direction',
      value: pipeSectionJSON.flow_direction,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: [
        {
          value: GeckoPipeFlowDirection.Unknown,
          label: 'Unknown',
        },
        {
          value: GeckoPipeFlowDirection.Positive,
          label: 'Positive',
        },
        {
          value: GeckoPipeFlowDirection.Negative,
          label: 'Negative',
        },
      ],
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    {
      dataKey: 'clock_bearing_rotation',
      label: 'Clock Rotation',
      value: pipeSectionJSON.clock_bearing_rotation,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'wall_thickness',
      label: 'Nominal Thickness',
      value: pipeSectionJSON.nominal.wall_thickness,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'tmin',
      label: 'Tmin',
      value: pipeSectionJSON.tmin,
      unit_magnitude: UnitMagnitude.InchesOrMillimeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: true,
      inputType: InputType.Number,
    },
    {
      dataKey: 'compass_orientation',
      label: 'Compass Orientation',
      value: pipeSectionJSON.compass_orientation,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const PipeSectionJSONFromFormFields = (
  component: PipeSectionAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedJointEfficiency = parseNumber(
    getFormFieldByDataKey(formFields, 'joint_efficiency')?.value
  )
  const parsedStressTolerance = parseNumber(
    getFormFieldByDataKey(formFields, 'stress_tolerance')?.value
  )
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const flowDirection = getFormFieldByDataKey(formFields, 'flow_direction')
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  return generatePipeSection(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'length')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    parsedJointEfficiency,
    parsedStressTolerance !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedStressTolerance,
          GeckoMeasurementUnit.Megapascals
        )
      : undefined,
    flowDirection?.value === 'undefined'
      ? undefined
      : (flowDirection?.value as GeckoPipeFlowDirection),
    parseNumber(
      getFormFieldByDataKey(formFields, 'clock_bearing_rotation')?.value
    ),
    parsedWallNominalThickness !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedWallNominalThickness,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parsedTmin !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTmin,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    compassOrientation,
    component
  )
}
